import type { MouseEvent } from "react";
import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { AuthProvider } from "./context/AuthProvider";
import { useAuth } from "./context/AuthProvider";
import { BulkSendProvider } from "./context/BulkSendContext";
import BulkSendWidget from "./components/BulkSendWidget/BulkSendWidget";
import { useLocation } from "react-router-dom";
import OnboardingModal from "./components/OnboardingModal/OnboardingModal";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DemoEntry from "src/demo/DemoEntry";
import DemoBanner from "src/demo/DemoBanner";
import UpdateBanner from "src/components/UpdateBanner/UpdateBanner";
import WhatsNewOnLogin from "src/components/WhatsNewPopup/WhatsNewOnLogin";
import { isDemoMode } from "src/demo/demoApi";
import { isStandard } from "src/utils/roles";
import { config } from "configs/config";

import Header from "./pages/Header/Header";
import { TrialEndModal } from "./components/TrialEndModal/TrialEndModal";
import QuickTips from "./components/QuickTips/QuickTips";
import ProtectedRoute from "./routes/ProtectedRoute";
import { PraxisSoftwareSyncProvider } from "src/context/PraxisSoftwareSyncContext";
import Login from "./pages/Login/Login";
import Register from "./pages/Register/Register";
import Main from "./pages/Main";
import NotFound from "./routes/NotFound";
import HomeRoute from "./routes/HomeRoute";
import Recipients from "./pages/Recipients/Recipients";
import Invoices from "./pages/Invoices/Invoices";
import Bank from "./pages/Bank/Bank";
import PraxisSoftwareSyncPage from "./pages/PraxisSoftwareSync/PraxisSoftwareSyncPage";
import RecipientPage from "./pages/RecipientPage/RecipientPage";
import InvoicePage from "./pages/InvoicePage/InvoicePage";
import InvoiceCreate from "./pages/InvoiceCreate/InvoiceCreate";
import Settings from "./pages/Settings/Settings";
import Dashboard from "./pages/Dashboard/Dashboard";
import Charges from "./pages/Charges/Charges";
import DocsPage from "./pages/Docs/DocsPage";
import Legal from "./pages/Legal/Legal";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import TrialActivated from "./pages/TrialActivated/TrialActivated";
import ForgotPassword from "./pages/Login/ForgotPassword";
import ResetPassword from "./pages/Login/ResetPassword";

function OnboardingGate() {
    const { user, updateUser } = useAuth();
    const location = useLocation();
    if (!user || user.doctor.avvSignedAt || location.pathname === "/legal" || isDemoMode()) return null;
    if (isStandard(user.doctor.userRights)) return null;
    return (
        <OnboardingModal
            onComplete={(avvSignedAt) =>
                updateUser({ doctor: { ...user.doctor, avvSignedAt } })
            }
        />
    );
}

function RedirectRecipientDetail() {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={id ? `/patients/${id}` : "/patients"} replace />;
}

function AvvModal({ onClose }: { onClose: () => void }) {
    const [src, setSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string>("");

    const load = useCallback(async () => {
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const res = await fetch(`${config.baseUrl}/api/doctors/avv-pdf`, {
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
            });
            setInfo(`HTTP ${res.status} · ${res.headers.get("content-type") ?? "?"}`);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setError(`HTTP ${res.status}: ${body.message ?? "Fehler beim Laden des AVV."}`);
                setLoading(false);
                return;
            }
            const blob = await res.blob();
            setInfo(`HTTP ${res.status} · ${blob.size} bytes · ${blob.type}`);
            if (blob.size === 0) {
                setError("PDF ist leer (0 Bytes). Backend-Container neu bauen.");
                setLoading(false);
                return;
            }
            setSrc(URL.createObjectURL(blob));
            setLoading(false);
        } catch (e: any) {
            setError("Netzwerkfehler: " + (e?.message ?? String(e)));
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const close = () => {
        if (src) URL.revokeObjectURL(src);
        onClose();
    };

    return createPortal(
        <div className="pdf-modal-overlay" onClick={close}>
            <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pdf-modal__header">
                    <span>Auftragsverarbeitungsvertrag (AVV){info && <span style={{ fontSize: 10, color: "#aaa", marginLeft: 10 }}>{info}</span>}</span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {src && (
                            <a href={src} download="avv_medinvoice.pdf" style={{ fontSize: 12, color: "#0277bd", textDecoration: "none" }}>
                                Herunterladen
                            </a>
                        )}
                        <button className="pdf-modal__close" onClick={close}>✕</button>
                    </div>
                </div>
                {loading && (
                    <div className="pdf-modal__loading">
                        <span className="pdf-modal__spinner" />
                    </div>
                )}
                {error && (
                    <div style={{ padding: "40px", textAlign: "center", color: "#666", fontSize: "14px" }}>
                        {error}
                    </div>
                )}
                {src && (
                    <iframe
                        src={src}
                        title="AVV"
                        style={{ flex: 1, width: "100%", border: "none", display: "block" }}
                    />
                )}
            </div>
        </div>,
        document.body,
    );
}

function AvvFooterButton() {
    const { user } = useAuth();
    const [showAvv, setShowAvv] = useState(false);
    const isLoggedIn = !!localStorage.getItem("token");
    const canSeeAvv = isLoggedIn && !isStandard(user?.doctor?.userRights);
    if (!canSeeAvv) return null;
    return (
        <>
            <button className="app-footer__avv-btn" onClick={() => setShowAvv(true)}>AVV</button>
            {showAvv && <AvvModal onClose={() => setShowAvv(false)} />}
        </>
    );
}

export default function App() {
    const location = useLocation();
    const { t } = useTranslation();
    // Include `/` so the HomeRoute redirect shell does not mount `Header` (which prefetches todo APIs even when logged out).
    const hideHeaderPaths = ["/", "/login", "/register", "/verify-email", "/legal", "/forgot-password", "/reset-password", "/trial-activated"];
    const showHeader = !hideHeaderPaths.includes(location.pathname);

    const inDemo = isDemoMode();
    const showDemoBanner = inDemo && showHeader;

    const skipToMain = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const main = document.getElementById("main-content");
        main?.focus({ preventScroll: true });
        main?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <AuthProvider>
          <BulkSendProvider>
          <PraxisSoftwareSyncProvider>
            <div className="app-container">
                <a href="#main-content" className="skip-link" onClick={skipToMain}>
                    {t("a11y.skipToContent")}
                </a>
                {showDemoBanner && <DemoBanner />}
                {showHeader && <UpdateBanner />}
                {showHeader && <WhatsNewOnLogin />}
                {showHeader ? <Header /> : null}
                <TrialEndModal />
                <OnboardingGate />
                {showHeader && <QuickTips />}
                <div className="app-main" id="main-content" tabIndex={-1}>
                <Routes>
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/trial-activated" element={<TrialActivated />} />
                    <Route path="/register" element={<Register />} />
                    {/* <Route
                        path="/main"
                        element={
                            <ProtectedRoute>
                                <Main />
                            </ProtectedRoute>
                        }
                    /> */}
                    <Route
                        path="/main"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/patients"
                        element={
                            <ProtectedRoute>
                                <Recipients />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/recipients" element={<Navigate to="/patients" replace />} />
                    <Route
                        path="/invoices"
                        element={
                            <ProtectedRoute>
                                <Invoices />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/bank"
                        element={
                            <ProtectedRoute>
                                <Bank />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/praxis-software-sync"
                        element={
                            <ProtectedRoute>
                                <PraxisSoftwareSyncPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/patients/:id"
                        element={
                            <ProtectedRoute>
                                <RecipientPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/recipients/:id" element={<RedirectRecipientDetail />} />
                    <Route
                        path="/invoices/new"
                        element={
                            <ProtectedRoute>
                                <InvoiceCreate />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoices/:id"
                        element={
                            <ProtectedRoute>
                                <InvoicePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <Settings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/charges"
                        element={
                            <ProtectedRoute>
                                <Charges />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/docs"
                        element={
                            <ProtectedRoute>
                                <DocsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/demo" element={<DemoEntry />} />
                    <Route path="/dashboard" element={<Navigate to="/main" replace />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                </div>
                <footer className="app-footer">
                    <span>{t("common.footer")}</span>
                    <span className="app-footer__legal">
                        <a href="/legal#agb" target="_blank" rel="noreferrer">AGB</a>
                        <a href="/legal#datenschutz" target="_blank" rel="noreferrer">Datenschutz</a>
                        <a href="/legal#impressum" target="_blank" rel="noreferrer">Impressum</a>
                        <AvvFooterButton />
                    </span>
                </footer>
            </div>
            <BulkSendWidget />
          </PraxisSoftwareSyncProvider>
          </BulkSendProvider>
        </AuthProvider>
    );
}
